import React, { useCallback, useEffect, useState } from 'react';
import { IVideo } from '../../types';
import {
  Card,
  FlexGrid,
  getClassNameFactory,
  Heading,
  kalturaSetupHandlerThunk,
  Link,
  TextBoldness,
  Typography,
} from '@dcl/ui';
import { boldTypes, types } from '@dcl/ui/dist/elements/Heading';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import { VideoIcon } from '../../../../icons/VideoIcon';
import { CourseIcon } from '../../../../icons/CourseIcon';
import { NewTabIcon } from '../../../../icons/NewTabIcon';
import fetchApi from '../../../../../../utils/fetchApi';
import { KALTURA_VIDEO_DATA_API } from '../../../../api';
import ReactHtmlParser from 'react-html-parser';
import './Video.css';
import { PDFIcon } from '../../../../icons/PDFIcon';
import { OPEN_PDF_FORMAT, PDF_FORMAT } from '../../../../mock';

export const Video: React.FC<IVideo> = (props) => {
  const { linkHref, linkText, format, isNewTab, entryId, ks } = props;
  const getClassName = getClassNameFactory(Video.displayName);

  const [videoData, setVideoData] = useState({
    videoTitle: '',
    videoDescription: '',
    videoDuration: '',
    videoViews: '',
    videoThumbnail: '',
    overRiddenFormat: '',
    overRiddenLabel: '',
  });

  const getArticleIcon = () => {
    const formatToUse = videoData.overRiddenFormat
      ? videoData.overRiddenFormat
      : format.toLocaleLowerCase();
    switch (formatToUse.toLocaleLowerCase()) {
      case 'video':
        return <VideoIcon />;
      case 'course':
        return <CourseIcon />;
      case 'pdf':
        return <PDFIcon />;
      default:
        return;
    }
  };

  const initiateVideodataApi = useCallback(async () => {
    const response = await fetchApi({
      baseHostUrl: KALTURA_VIDEO_DATA_API,
      options: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entryId: entryId,
          version: -1,
          ks: ks,
          format: 1,
        }),
      },
    });

    const videoMetaData = await response.text();
    const data = JSON.parse(videoMetaData);

    !data?.code &&
      setVideoData((previousInput) => ({
        ...previousInput,
        videoTitle: data?.name,
        videoDescription: data?.description,
        videoDuration: data?.duration,
        videoViews: data?.views,
        videoThumbnail: data?.thumbnailUrl,
        overRiddenLabel: data?.searchText.includes('__pdfentry')
          ? OPEN_PDF_FORMAT
          : linkText,
        overRiddenFormat: data?.searchText.includes('__pdfentry')
          ? PDF_FORMAT
          : format,
      }));
  }, [entryId, format, ks, linkText]);

  useEffect(() => {
    initiateVideodataApi();
  }, [initiateVideodataApi]);

  ks.length > 0 && kalturaSetupHandlerThunk();

  const getArticleSize = videoData.videoDuration && (
    <Typography
      tag={TypographyTags.SPAN}
      boldness={TextBoldness.Light}
      className={getClassName({ descendantName: 'articleFormat' })}
    >
      {videoData.videoDuration}
    </Typography>
  );

  const segmentationRow = (
    <div className={getClassName({ descendantName: 'Info' })}>
      <div className={getClassName({ descendantName: 'Icon' })}>
        {getArticleIcon()}
      </div>
      <Typography
        tag={TypographyTags.SPAN}
        boldness={TextBoldness.Light}
        className={getClassName({
          descendantName: 'articleFormat',
        })}
      >
        {videoData.overRiddenFormat}
      </Typography>
      {videoData.overRiddenFormat === PDF_FORMAT ? '' : getArticleSize}
      <Typography tag={TypographyTags.SPAN} boldness={TextBoldness.Regular}>
        {videoData.overRiddenFormat === PDF_FORMAT
          ? ''
          : `${videoData.videoViews} views`}
      </Typography>
    </div>
  );

  return (
    <div className={getClassName({ descendantName: 'tile' })}>
      <Card.CardBase size="small" data-testid="ArticleTile">
        <div className={getClassName({ descendantName: 'imageConatiner' })}>
          <img
            src={videoData.videoThumbnail}
            alt={'hero'}
            className={getClassName({ descendantName: 'image' })}
          />
        </div>

        <FlexGrid
          className={getClassName({ descendantName: 'cardContentContainer' })}
        >
          <FlexGrid.Row>
            <FlexGrid.Col
              xs={12}
              sm={12}
              md={12}
              className={getClassName({ descendantName: 'articleHeading' })}
            >
              <Heading
                type={types.Medium}
                boldType={boldTypes.medium}
                className={getClassName({
                  descendantName: 'articleTitleHeading',
                })}
              >
                <div title={videoData.videoTitle}>{videoData.videoTitle}</div>
              </Heading>
            </FlexGrid.Col>
          </FlexGrid.Row>
          <FlexGrid.Row className={getClassName({ descendantName: 'eyeBrow' })}>
            <FlexGrid.Col xs={3} sm={3} md={3}>
              <Typography
                tag={TypographyTags.SPAN}
                boldness={TextBoldness.Regular}
                className={getClassName({ descendantName: 'articleFormat' })}
              >
                {'Format'}:
              </Typography>
            </FlexGrid.Col>

            <FlexGrid.Col xs={9} sm={9} md={9}>
              {segmentationRow}
            </FlexGrid.Col>
          </FlexGrid.Row>

          <FlexGrid.Row
            className={getClassName({ descendantName: 'description' })}
          >
            <FlexGrid.Col xs={12} sm={12} md={12}>
              <Typography
                tag={TypographyTags.SPAN}
                boldness={TextBoldness.Regular}
                className={getClassName({
                  descendantName: 'articleDescription',
                })}
              >
                <div title={videoData.videoDescription}>
                  {ReactHtmlParser(videoData.videoDescription)}
                </div>
              </Typography>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
        <FlexGrid.Row className={getClassName({ descendantName: 'link' })}>
          <FlexGrid.Col xs={12} sm={12} md={12}>
            {isNewTab ? (
              <Link
                href={linkHref}
                newWindow={isNewTab}
                iconRight={<NewTabIcon />}
              >
                {videoData.overRiddenLabel}
              </Link>
            ) : (
              <Link href={linkHref} newWindow={isNewTab}>
                {videoData.overRiddenLabel}
              </Link>
            )}
          </FlexGrid.Col>
        </FlexGrid.Row>
      </Card.CardBase>
    </div>
  );
};

Video.displayName = 'Video';
